/**
 * InProgressCard — in-progress session card in TestsHub.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/tests.jsx InProgressCard
 * Gold left rail, resume/discard actions.
 */

import * as React from 'react'
import { fmtDate, fmtTime } from '@arianna/reader-core'

export interface InProgressCardSession {
  id: string
  started_at: number
  current_idx: number
  num_questions: number
}

export interface InProgressCardProps {
  session: InProgressCardSession
  onResume: () => void
  onDiscard: () => void
}

export function InProgressCard({ session, onResume, onDiscard }: InProgressCardProps) {
  // TASK-015: the server no longer sends a per-question answers map in the
  // summary (only already-completed quizzes expose per-question data, per
  // invariant 1 — see QuizPresenter). `current_idx` is a bookmark (last
  // VIEWED position), not an answered-count — Phase 2 adds free navigation,
  // so a user who jumps back before leaving can make this look lower than
  // how much of the quiz they've actually answered. There is no per-quiz
  // answered/skipped count in the hub summary to do better with (exposing
  // one wouldn't violate invariant 1, but the contract doesn't carry it —
  // `score` itself stays null until completion), so this stays a reasonable
  // approximation for the common "answer forward, then leave" path rather
  // than an exact count.
  const touched = session.current_idx
  const pctDone =
    session.num_questions > 0 ? Math.round((touched / session.num_questions) * 100) : 0

  return (
    <div
      className="bg-surface border border-gold rounded-[12px] overflow-hidden flex font-sans"
      /* design handoff: gold glow ring via color-mix — inline style for color-mix */
      style={{
        boxShadow: 'var(--shadow-sm), 0 0 0 3px color-mix(in oklab, var(--gold) 10%, transparent)',
      }}
    >
      {/* Gold left rail */}
      <div className="w-[3px] bg-gold shrink-0" />

      <div className="flex-1 px-5 py-4 min-w-0">
        {/* Status label + timestamp */}
        <div className="flex items-center gap-2 mb-[6px]">
          <span className="text-[10px] text-gold tracking-[0.12em] uppercase font-semibold">
            In sospeso
          </span>
          {/* design handoff: dot 2.5px */}
          <span className="w-[2.5px] h-[2.5px] rounded-full bg-ink-4 shrink-0 inline-block" />
          {/* design handoff: fontSize 10.5px */}
          <span className="font-mono text-[10.5px] text-ink-4">
            {fmtDate(session.started_at)} · {fmtTime(session.started_at)}
          </span>
        </div>

        {/* design handoff: fontSize 13.5px */}
        <div className="text-[13.5px] text-ink font-medium mb-[10px]">
          Fermata alla domanda {session.current_idx + 1} di {session.num_questions}
        </div>

        {/* Progress bar */}
        {/* design handoff: maxWidth 220px, height 3px */}
        <div className="w-full max-w-[220px] h-[3px] bg-surface-3 rounded-sm overflow-hidden mb-[5px]">
          <div
            className="h-full bg-gold rounded-sm transition-[width] duration-[400ms] ease-[ease]"
            style={{ width: `${pctDone}%` }}
          />
        </div>
        <div className="text-[11px] text-ink-3">
          {touched} di {session.num_questions} domande affrontate
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 items-center px-5 shrink-0">
        <button
          onClick={onDiscard}
          /* design handoff: fontSize 11.5px, borderRadius 7px */
          className="px-3 py-[7px] text-[11.5px] font-medium bg-transparent text-ink-4 border border-line rounded-[7px] cursor-pointer font-sans"
        >
          Scarta
        </button>
        <button
          onClick={onResume}
          className="px-[18px] py-[10px] text-sm font-semibold bg-ink text-bg border border-ink rounded-md cursor-pointer font-sans flex items-center gap-[7px] whitespace-nowrap"
        >
          ▶ Riprendi
        </button>
      </div>
    </div>
  )
}
